import { useMemo } from 'react';
import { ComboboxConfig } from './ComboboxConfig';
import { useConfigLookup } from '@/hooks/useConfigLookup';
import type { FKOption } from '@/hooks/useFKLookup';
import { trpcClient } from '@/lib/trpc';
import { useQueryClient } from '@tanstack/react-query';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { toast } from 'sonner';

interface ComboboxConfigMasterProps {
    categoryCode: string;
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
    disabled?: boolean;
    className?: string;
    hideLabel?: boolean;
    canAdd?: boolean; // Override if needed
    valueField?: 'code' | 'name'; // Lưu giá trị theo code hoặc name (chuỗi hiển thị)
    filterOptions?: (option: FKOption) => boolean;
}

/** Danh mục cố định theo enum DB (kể cả tên gọi khác) — chỉ chọn, không thêm mục mới */
const ENUM_BACKED_CATEGORIES = new Set([
    'deployment_model', 'priority', 'mold_type', 'mold_types', 'tooling_status', 'machine_type', 'machine_types',
]);

export function ComboboxConfigMaster({
    categoryCode,
    value,
    onChange,
    label,
    placeholder,
    required,
    error,
    disabled,
    className,
    hideLabel,
    canAdd,
    valueField = 'code',
    filterOptions,
}: ComboboxConfigMasterProps) {
    const { options, isLoading } = useConfigLookup(categoryCode, { valueField });
    const filteredOptions = useMemo(
        () => (filterOptions ? options.filter(filterOptions) : options),
        [options, filterOptions],
    );
    const queryClient = useQueryClient();
    const { hasPermission, role } = useRoleAccess();
    
    // Dynamic Contextual RBAC Resolver
    const isAdmin = role === 'ADMIN' || hasPermission('*', '*');
    const hasCreatePerm = useMemo(() => {
        // Danh mục gắn enum DB: không thêm nhanh (máy chủ cũng chặn) — giá trị ngoài enum làm form lưu lỗi
        if (ENUM_BACKED_CATEGORIES.has(categoryCode.toLowerCase())) return false;
        // Đơn vị tính: chỉ thêm ở Cấu hình (tên chuẩn + bí danh) — thêm nhanh từng sinh ĐVT trùng / rác (04/11/2026)
        if (categoryCode.toLowerCase() === 'units') return false;
        // Thêm mục danh mục = thêm dữ liệu gốc: chỉ người có quyền Thêm ở Cấu hình (máy chủ cũng kiểm — 05/11/2026),
        // tránh ai cũng thêm mục rác. canAdd chỉ dùng để tắt thêm nhanh ở một form, không mở thêm quyền.
        if (canAdd === false) return false;
        return isAdmin || hasPermission('configs', 'create');
    }, [canAdd, isAdmin, hasPermission, categoryCode]);

    const handleQuickAdd = async (name: string) => {
        const trimmedName = name.trim();
        if (!trimmedName) return null;

        // Prevent duplicate addition locally
        const existing = options.find(
            opt => opt.label.trim().toLowerCase() === trimmedName.toLowerCase() ||
                   opt.id.trim().toLowerCase() === trimmedName.toLowerCase()
        );
        if (existing) {
            toast.warning(`"${trimmedName}" đã có trong danh mục`);
            return existing.id;
        }

        try {
            // Generate clean code fallback
            const generatedCode = trimmedName
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                .toUpperCase()
                .replace(/[^A-Z0-9_-]/g, '_')
                .replace(/_+/g, '_');

            const newItem = await trpcClient.config.addConfigItem.mutate({
                categoryCode: categoryCode,
                name: trimmedName,
                code: generatedCode || trimmedName,
                sortOrder: options.length,
            });

            // Invalidate the query so it re-fetches across all consumers
            await queryClient.invalidateQueries({ queryKey: ['config-lookup'] });
            await queryClient.invalidateQueries({ queryKey: ['trpc', 'config'] });
            toast.success(`Đã thêm "${trimmedName}" vào danh mục`);
            
            // Nếu dùng valueField = 'name' thì trả về name vừa tạo để form điền đúng text
            return valueField === 'name' ? (newItem.name || trimmedName) : (newItem.code || newItem.name);
        } catch (error: any) {
            toast.error(error.message || 'Lỗi khi thêm danh mục');
            return null;
        }
    };

    return (
        <div className={className}>
            <ComboboxConfig
                value={value}
                onChange={onChange}
                options={filteredOptions}
                label={label}
                placeholder={placeholder}
                required={required}
                loading={isLoading}
                error={error}
                disabled={disabled}
                hideLabel={hideLabel}
                quickAddLabel="Thêm mới"
                onQuickAdd={handleQuickAdd}
                canAdd={hasCreatePerm} // Restrict quick add to users with create permission
            />
        </div>
    );
}
